/**
 * Formato en vivo de los campos de dinero: `130000` se ve como `130.000` mientras se escribe.
 *
 * Hay DOS representaciones y no se mezclan nunca:
 *
 *  - el valor CANÓNICO, que es el que guarda el formulario y viaja a la API: `"130000.5"`. Sin
 *    millares y con punto decimal, igual que `numeric(14,2)` en la base.
 *  - el TEXTO del campo, que es lo que ve la persona: `"130.000,5"`. Punto de millar y coma
 *    decimal, como se escribe en España.
 *
 * El texto se DERIVA siempre del canónico (`formatMoneyDisplay`), así que lo que vuelve del
 * servidor (`"130000.00"`) se pinta igual que lo que se teclea.
 *
 * El canónico conserva el estado a medias de quien teclea —`"130000."` tras escribir la coma—,
 * porque si no la coma desaparecería en cuanto se pulsa y no habría forma de escribir céntimos.
 *
 * Funciones puras a propósito: la parte con cursor y eventos vive en `MoneyInput`.
 */

/** Canónico → texto. `"1234567.5"` → `"1.234.567,5"`. */
export function formatMoneyDisplay(canonical: string): string {
  if (!canonical) return '';
  const negative = canonical.startsWith('-');
  const body = negative ? canonical.slice(1) : canonical;
  const dot = body.indexOf('.');
  const integer = dot === -1 ? body : body.slice(0, dot);
  const decimals = dot === -1 ? undefined : body.slice(dot + 1);
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (negative ? '-' : '') + grouped + (decimals === undefined ? '' : `,${decimals}`);
}

/**
 * Texto → canónico. Los puntos del texto son SIEMPRE de millar y se descartan; la coma es el
 * decimal. Como mucho dos decimales: el tercero se ignora en vez de redondear a escondidas.
 */
export function parseMoneyDisplay(text: string): string {
  const negative = text.trim().startsWith('-');
  const clean = text.replace(/[^\d,]/g, '');
  const comma = clean.indexOf(',');
  let integer = comma === -1 ? clean : clean.slice(0, comma);
  const decimals = comma === -1 ? undefined : clean.slice(comma + 1).replace(/,/g, '').slice(0, 2);
  integer = integer.replace(/^0+(?=\d)/, '');
  if (decimals !== undefined && integer === '') integer = '0';
  if (integer === '' && decimals === undefined) return negative ? '-' : '';
  return (negative ? '-' : '') + integer + (decimals === undefined ? '' : `.${decimals}`);
}

/**
 * Lo pegado de fuera puede traer el punto como decimal (`1250.50`, copiado de un Excel en
 * inglés o de un extracto). Un punto seguido de UNO o DOS dígitos al final no puede ser de
 * millar —esos van de tres en tres—, así que se lee como decimal.
 * Solo para pegar: al teclear, `1.23` es un `1.234` al que le acabas de borrar el 4.
 */
export function normalizePastedMoney(text: string): string {
  if (text.includes(',')) return text;
  return text.replace(/\.(\d{1,2})$/, ',$1');
}

/** Cuántos caracteres con significado (dígitos, coma y signo) hay en un texto. */
export function significantCount(text: string): number {
  return text.replace(/[^\d,-]/g, '').length;
}

/**
 * Posición del cursor en `formatted` que deja a su izquierda `count` caracteres con
 * significado. Es lo que evita que el cursor salte al final cada vez que aparece un punto.
 */
export function caretForSignificant(formatted: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    if (/[\d,-]/.test(formatted[index]!)) seen += 1;
    if (seen === count) return index + 1;
  }
  return formatted.length;
}

/**
 * Suma importes canónicos (`"1250.50"`) sin pasar por coma flotante: se suman CÉNTIMOS
 * enteros. `0.1 + 0.2` en `number` da `0.30000000000000004`; aquí da `"0.30"`.
 * Lo vacío o ilegible cuenta como cero.
 */
export function sumMoney(values: Array<string | null | undefined>): string {
  let cents = 0;
  for (const value of values) {
    const parsed = Number(value);
    if (value && Number.isFinite(parsed)) cents += Math.round(parsed * 100);
  }
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}
